'use client';

import { c as _c } from "react/compiler-runtime";
import { type FormEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LABS_INTERVIEW_EVENTS, type LabsInterviewEventName } from '@/lib/analytics/labs-interview-events';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { CONSENT_DIALOG_HIDDEN_CSS, isConsentDialogSuppressed } from '@/lib/consent/embedded-consent-dialog';
import { getPreviewVisitorId } from '../../[vertical]/_engine/anonymous-id';
import { isAutomatedBrowser } from '../../[vertical]/_engine/automated-client';
import { isEmbedded, readOpenedInModalFromLocation, readPlacementFromLocation } from '../../[vertical]/_engine/entry-placement';
import { readGuideSourceFromLocation } from '../../[vertical]/_engine/guide-source';
import { submitPreviewLead } from '../../[vertical]/_engine/submit-preview-lead';
import { groundedPack, pageGrounding } from '../_content/grounding';
import { type InterviewPack, type PackKey, SITUATIONS, type StoryChoice } from '../_content/packs';
import { readCompanyFromLocation, readFirstAnswerFromLocation, withCompany } from '../_engine/interview-params';
import { interviewSignUpHref } from '../_engine/interview-sign-up-href';
import { DECISION_LABEL, type DecisionKind, decide, type Level, skillLevel, storyLevel, storyReaction } from '../_engine/scoring';

/**
 * The mock first-round interview. One client tree, four phases:
 *
 *   hook     the first question with its answers (only when the guide page did not already ask it)
 *   chat     the interview itself: two skill rounds with a follow-up or a hint, then the story builder
 *   review   a two-second "filling in your scorecard" beat, so the verdict feels earned
 *   score    the interviewer's scorecard, the email unlock, and the externship that fills the gap
 *
 * The chat is a SCRIPT, not a state machine: it is written top to bottom as `await say()` /
 * `await ask()`, which is how an interview reads, and it is cancelled by a run token when the reader
 * restarts. Every screen is laid out as `100dvh`, because on a phone it runs as a full-screen modal
 * and on a desktop inside a fixed 700x850 frame (see the snippet), so it always owns a viewport.
 *
 * No interviewer names (Jay, Sep 22): "your interviewer" only, and the scorecard says the format is
 * modelled on a first round and not affiliated with the company.
 */

type MsgKind = 'coach' | 'draft' | 'me' | 'q' | 'sys' | 'them' | 'typing';
interface Msg {
  after?: string;
  body?: string;
  id: number;
  kind: MsgKind;
  text?: string;
  title?: string;
  tone?: 'bad' | 'good' | 'meh';
  yours?: string;
}
interface Dock {
  builderStep?: number;
  hint?: string;
  options: string[];
  solid?: boolean;
  timed?: boolean;
  tip?: boolean;
}
type Phase = 'chat' | 'hook' | 'review' | 'score';
const EASE = 'cubic-bezier(.22,1,.36,1)';
const KEYFRAMES = `
@keyframes xi-pop{from{opacity:0;transform:translateY(6px) scale(.98)}to{opacity:1;transform:none}}
@keyframes xi-blink{0%,80%,100%{opacity:.3}40%{opacity:1}}
@keyframes xi-stamp{from{opacity:0;transform:rotate(-2.5deg) scale(1.6)}to{opacity:1;transform:rotate(-2.5deg) scale(1)}}
.xi-pop{animation:xi-pop .26s ${EASE}}
@media (prefers-reduced-motion: reduce){.xi-pop{animation:none}}
`;
const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));
function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/** A dock pick back to its choice. Only ever called with an index the dock itself offered. */
function pickFrom<T>(list: readonly T[], index: number): T {
  const item = list[index];
  if (item === undefined) {
    throw new Error(`No choice at index ${index}`);
  }
  return item;
}
const STORY_COACH_TITLE: Record<Level, string> = {
  missing: '✗ Story evidence: missing',
  ready: '✓ Story evidence',
  shaky: '~ Story evidence: thin'
};
const LEVEL_TONE: Record<Level, 'bad' | 'good' | 'meh'> = {
  missing: 'bad',
  ready: 'good',
  shaky: 'meh'
};
function progressColour(segment: number, round: number): string {
  if (segment < round) {
    return 'bg-[#4ecb5f]';
  }
  return segment === round ? 'bg-[#4ecb5f] shadow-[0_0_0_3px_rgba(78,203,95,.18)]' : 'bg-[#e6e7ea]';
}
function scorecardTitle({
  allReady,
  skillsStrong,
  storyReady
}: {
  allReady: boolean;
  skillsStrong: boolean;
  storyReady: boolean;
}): string {
  if (allReady) {
    return 'You’d make it to round two.';
  }
  if (storyReady) {
    return 'You’re close. Your story carries you.';
  }
  return skillsStrong ? 'You’ve got the skills. You need the story.' : 'You’re one fix away from a real shot.';
}

/**
 * Renders nothing on the server. What the reader sees depends on the URL (`?a1=` decides whether the
 * interview opens on the hook or mid-chat, `?co=` fills every line), and the page is statically
 * rendered without it. Rendering the tree on the server would hydrate the hook screen and then swap
 * it for the chat, a hydration mismatch on the very first paint. A blank white frame for one tick
 * is invisible inside the guide page's opener, which is already showing its own transition.
 */
export function InterviewApp(t0) {
  const $ = _c(5);
  const {
    packKey
  } = t0;
  const [mounted, setMounted] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setMounted(true);
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== mounted || $[3] !== packKey) {
    t3 = mounted ? <InterviewRoot packKey={packKey} /> : <div className="h-dvh bg-white" />;
    $[2] = mounted;
    $[3] = packKey;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function InterviewRoot(t0) {
  const $ = _c(70);
  const {
    packKey
  } = t0;
  const [company] = useState(readCompanyFromLocation);
  const [guideSource] = useState(readGuideSourceFromLocation);
  let t1;
  if ($[0] !== guideSource || $[1] !== packKey) {
    t1 = () => groundedPack(packKey, pageGrounding(guideSource));
    $[0] = guideSource;
    $[1] = packKey;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [pack] = useState(t1);
  const [placement] = useState(readPlacementFromLocation);
  const [visitorId] = useState(getPreviewVisitorId);
  let t2;
  if ($[3] !== pack.qs[0].options.length) {
    t2 = () => readFirstAnswerFromLocation(pack.qs[0].options.length);
    $[3] = pack.qs[0].options.length;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const [firstAnswer] = useState(t2);
  let t3;
  if ($[5] !== placement) {
    t3 = isEmbedded(placement);
    $[5] = placement;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const embedded = t3;
  let t4;
  if ($[7] !== embedded) {
    t4 = () => embedded && readOpenedInModalFromLocation();
    $[7] = embedded;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const [closeInset] = useState(t4);
  let t5;
  if ($[9] !== firstAnswer) {
    t5 = () => firstAnswer === null ? "hook" : "chat";
    $[9] = firstAnswer;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const [phase, setPhase] = useState(t5);
  const [openingPick, setOpeningPick] = useState(firstAnswer);
  const [runId, setRunId] = useState(0);
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const [levels, setLevels] = useState(t6);
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const [notes, setNotes] = useState(t7);
  const {
    track: platformTrack
  } = useAnalytics();
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = isAutomatedBrowser();
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const automated = t8;
  let t9;
  if ($[14] !== company || $[15] !== firstAnswer || $[16] !== guideSource || $[17] !== pack.dims || $[18] !== packKey || $[19] !== placement || $[20] !== platformTrack || $[21] !== visitorId) {
    t9 = (event, props) => {
      if (automated) {
        return;
      }
      platformTrack(event, {
        company,
        entry: firstAnswer === null ? "link" : "question",
        rounds: [pack.dims[0], pack.dims[1]],
        guide_source: guideSource,
        pack: packKey,
        placement,
        preview_visitor_id: visitorId,
        ...props
      });
    };
    $[14] = company;
    $[15] = firstAnswer;
    $[16] = guideSource;
    $[17] = pack.dims;
    $[18] = packKey;
    $[19] = placement;
    $[20] = platformTrack;
    $[21] = visitorId;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const track = t9;
  let t10;
  if ($[23] !== company) {
    t10 = text => withCompany(text, company);
    $[23] = company;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t = t10;
  const styleName = company ? `${company}-style first round` : "First-round practice";
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [];
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  useEffect(_temp, t11);
  const viewed = useRef(false);
  let t12;
  let t13;
  if ($[26] !== firstAnswer || $[27] !== track) {
    t12 = () => {
      if (viewed.current) {
        return;
      }
      viewed.current = true;
      track(LABS_INTERVIEW_EVENTS.VIEWED);
      if (firstAnswer !== null) {
        track(LABS_INTERVIEW_EVENTS.STARTED, {
          from: "guide_page"
        });
      }
    };
    t13 = [firstAnswer, track];
    $[26] = firstAnswer;
    $[27] = track;
    $[28] = t12;
    $[29] = t13;
  } else {
    t12 = $[28];
    t13 = $[29];
  }
  useEffect(t12, t13);
  let t14;
  if ($[30] !== track) {
    t14 = pick => {
      track(LABS_INTERVIEW_EVENTS.STARTED, {
        from: "hook"
      });
      setOpeningPick(pick);
      setPhase("chat");
    };
    $[30] = track;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const startFromHook = t14;
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <style>{KEYFRAMES}</style>;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== closeInset || $[34] !== company || $[35] !== pack || $[36] !== phase || $[37] !== startFromHook || $[38] !== t) {
    t16 = phase === "hook" ? <Hook closeInset={closeInset} company={company} onPick={startFromHook} pack={pack} t={t} /> : null;
    $[33] = closeInset;
    $[34] = company;
    $[35] = pack;
    $[36] = phase;
    $[37] = startFromHook;
    $[38] = t;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== closeInset || $[41] !== openingPick || $[42] !== pack || $[43] !== phase || $[44] !== runId || $[45] !== styleName || $[46] !== t || $[47] !== track) {
    t17 = phase === "chat" && openingPick !== null ? <Chat closeInset={closeInset} firstPick={openingPick} key={runId} onDone={(finalLevels, finalNotes) => {
      setLevels(finalLevels);
      setNotes(finalNotes);
      setPhase("review");
    }} pack={pack} styleName={styleName} t={t} track={track} /> : null;
    $[40] = closeInset;
    $[41] = openingPick;
    $[42] = pack;
    $[43] = phase;
    $[44] = runId;
    $[45] = styleName;
    $[46] = t;
    $[47] = track;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== pack.dims || $[50] !== phase) {
    t18 = phase === "review" ? <Review dims={pack.dims} onDone={() => setPhase("score")} /> : null;
    $[49] = pack.dims;
    $[50] = phase;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== closeInset || $[53] !== company || $[54] !== embedded || $[55] !== guideSource || $[56] !== levels || $[57] !== notes || $[58] !== pack || $[59] !== phase || $[60] !== styleName || $[61] !== t || $[62] !== track || $[63] !== visitorId) {
    t19 = phase === "score" ? <Scorecard closeInset={closeInset} company={company} embedded={embedded} guideSource={guideSource} levels={levels} notes={notes} onRestart={() => {
      setRunId(_temp2);
      setOpeningPick(null);
      setPhase("hook");
    }} pack={pack} styleName={styleName} t={t} track={track} visitorId={visitorId} /> : null;
    $[52] = closeInset;
    $[53] = company;
    $[54] = embedded;
    $[55] = guideSource;
    $[56] = levels;
    $[57] = notes;
    $[58] = pack;
    $[59] = phase;
    $[60] = styleName;
    $[61] = t;
    $[62] = track;
    $[63] = visitorId;
    $[64] = t19;
  } else {
    t19 = $[64];
  }
  let t20;
  if ($[65] !== t16 || $[66] !== t17 || $[67] !== t18 || $[68] !== t19) {
    t20 = <div className="flex h-dvh flex-col overflow-hidden bg-white text-[#1c1b1b] antialiased">{t15}{t16}{t17}{t18}{t19}</div>;
    $[65] = t16;
    $[66] = t17;
    $[67] = t18;
    $[68] = t19;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  return t20;
}

/* ---------------------------------------------------------------- hook */
function _temp2(n) {
  return n + 1;
}
function _temp() {
  if (!isConsentDialogSuppressed()) {
    return;
  }
  const style = document.createElement("style");
  style.setAttribute("data-consent-dialog", "hidden-in-frame");
  style.textContent = CONSENT_DIALOG_HIDDEN_CSS;
  document.head.appendChild(style);
  return () => style.remove();
}
function Hook(t0) {
  const $ = _c(26);
  const {
    closeInset,
    company,
    onPick,
    pack,
    t
  } = t0;
  const q = pack.qs[0];
  const t1 = `xi-pop flex flex-1 flex-col overflow-y-auto px-5 pb-5 ${closeInset ? "pt-[60px]" : "pt-6"}`;
  let t2;
  if ($[0] !== pack.track) {
    t2 = <p className="font-semibold text-[#8a8785] text-[14px] uppercase tracking-[.06em]">Extern Labs · {pack.track}</p>;
    $[0] = pack.track;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = company ? `Think you’re ready for an interview at ${company}?` : "Think you\u2019re ready for a first-round interview?";
  let t4;
  if ($[2] !== t3) {
    t4 = <h1 className="mt-2 mb-2 font-extrabold text-[34px] leading-[1.08] tracking-[-1px]">{t3}</h1>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="mb-5 text-[#5c5a58] text-[17px] leading-[1.45]">Three questions, the kind first rounds really ask, with honest feedback on every answer.</p>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== q.prompt || $[6] !== t) {
    t6 = t(q.prompt);
    $[5] = q.prompt;
    $[6] = t;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <div className="mb-3 rounded-[18px_18px_18px_6px] border border-[#e5e2e1] bg-white px-4 py-3.5 font-semibold text-[19px] leading-[1.35] shadow-[0_2px_10px_rgba(0,0,0,.04)]">“{t6}”</div>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== onPick || $[11] !== q.options || $[12] !== t) {
    let t9;
    if ($[14] !== onPick || $[15] !== t) {
      t9 = (option, i) => <button className="flex items-center gap-2.5 rounded-[14px] border-[#e5e2e1] border-[1.5px] bg-white px-3.5 py-3 text-left font-medium text-[16px] leading-[1.35] transition-colors hover:border-[#2853d2]" key={option} onClick={() => onPick(i)} type="button"><span className="grid size-[22px] flex-none place-items-center rounded-full border-[#c9c6c5] border-[1.5px] font-bold text-[#5c5a58] text-[12px]">{"ABCD"[i]}</span>{t(option)}</button>;
      $[14] = onPick;
      $[15] = t;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    t8 = q.options.map(t9);
    $[10] = onPick;
    $[11] = q.options;
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <div className="grid gap-2">{t8}</div>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <p className="mt-4 text-center text-[#8a8785] text-[14px]">90 seconds · free · no sign-up</p>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t1 || $[21] !== t2 || $[22] !== t4 || $[23] !== t7 || $[24] !== t9) {
    t11 = <div className={t1}>{t2}{t4}{t5}{t7}{t9}{t10}</div>;
    $[20] = t1;
    $[21] = t2;
    $[22] = t4;
    $[23] = t7;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
}

/* ---------------------------------------------------------------- chat */

function Chat(t0) {
  const $ = _c(39);
  const {
    closeInset,
    firstPick,
    onDone,
    pack,
    styleName,
    t,
    track
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [msgs, setMsgs] = useState(t1);
  const [dock, setDock] = useState(null);
  const [round, setRound] = useState(0);
  const [clockKey, setClockKey] = useState(0);
  const threadRef = useRef(null);
  const resolver = useRef(null);
  const echo = useRef(true);
  const nextId = useRef(0);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      threadRef.current?.scrollTo({
        behavior: "smooth",
        top: threadRef.current.scrollHeight
      });
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== dock || $[3] !== msgs) {
    t3 = [msgs, dock];
    $[2] = dock;
    $[3] = msgs;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  const pick = (i, label) => {
    const resolve = resolver.current;
    if (!resolve) {
      return;
    }
    resolver.current = null;
    setDock(null);
    if (echo.current && !dock?.solid) {
      setMsgs(m => {
        nextId.current = nextId.current + 1;
        return [...m, {
          id: nextId.current,
          kind: "me",
          text: label
        }];
      });
    }
    resolve(i);
  };
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  useEffect(() => {
    let cancelled = false;
    const fast = prefersReducedMotion();
    const push = m_0 => {
      if (!cancelled) {
        setMsgs(all => {
          nextId.current = nextId.current + 1;
          return [...all, {
            ...m_0,
            id: nextId.current
          }];
        });
      }
    };
    const pause = ms => wait(fast ? Math.min(ms, 80) : ms);
    const say = async function say(text, t4) {
      const kind = t4 === undefined ? "them" : t4;
      if (cancelled) {
        throw new Error("cancelled");
      }
      nextId.current = nextId.current + 1;
      const typingId = nextId.current;
      setMsgs(all_0 => [...all_0, {
        id: typingId,
        kind: "typing"
      }]);
      await pause(Math.min(1200, 450 + text.length * 8));
      if (cancelled) {
        throw new Error("cancelled");
      }
      setMsgs(all_1 => {
        nextId.current = nextId.current + 1;
        return all_1.filter(m_1 => m_1.id !== typingId).concat({
          id: nextId.current,
          kind,
          text
        });
      });
      await pause(220);
    };
    const ask = function ask(d, t5) {
      const shouldEcho = t5 === undefined ? true : t5;
      if (cancelled) {
        return Promise.reject(new Error("cancelled"));
      }
      echo.current = shouldEcho;
      if (d.timed) {
        setClockKey(_temp3);
      }
      setDock(d);
      return new Promise(resolve_0 => {
        resolver.current = resolve_0;
      });
    };
    const skillRound = async function skillRound(i_0, preset) {
      const q = pack.qs[i_0];
      const choice = preset ?? (await ask({
        hint: "Tap your answer",
        options: q.options.map(t),
        timed: true
      }));
      const firstTry = choice === q.correct;
      track(LABS_INTERVIEW_EVENTS.ANSWERED, {
        attempt: 1,
        correct: firstTry,
        round: i_0 + 1,
        step: "question"
      });
      if (firstTry) {
        await say(t(q.good));
        await say(t(q.fu.prompt), "q");
        const f = await ask({
          hint: "Follow-up",
          options: q.fu.options.map(t),
          timed: true
        });
        const ok = f === q.fu.correct;
        track(LABS_INTERVIEW_EVENTS.ANSWERED, {
          attempt: 1,
          correct: ok,
          round: i_0 + 1,
          step: "follow_up"
        });
        await say(t(ok ? q.fu.good : q.fu.bad));
        push({
          body: `${t(q.why)} ${t(q.fu.why)}`,
          kind: "coach",
          title: ok ? `✓ ${q.skill}` : `~ ${q.skill}: strong start`,
          tone: ok ? "good" : "meh"
        });
        const level = skillLevel({
          firstTry,
          followUp: ok
        });
        return [level, ok ? q.noteGood : q.noteFu];
      }
      await say(`${t(q.bad)} Want to take another shot?`);
      const rest = q.options.map(_temp4).filter(k_1 => k_1 !== choice);
      const retryIdx = await ask({
        hint: `💡 Hint: ${t(q.hint)}`,
        options: rest.map(k_2 => t(q.options[k_2] ?? "")),
        timed: true,
        tip: true
      });
      const ok_0 = rest[retryIdx] === q.correct;
      track(LABS_INTERVIEW_EVENTS.ANSWERED, {
        attempt: 2,
        correct: ok_0,
        round: i_0 + 1,
        step: "question"
      });
      await say(ok_0 ? "There you go. That\u2019s the one." : `The answer I was after: “${t(q.options[q.correct] ?? "")}.”`);
      push({
        body: t(q.why),
        kind: "coach",
        title: ok_0 ? `~ ${q.skill}: with a hint` : `✗ ${q.skill}: what they wanted`,
        tone: ok_0 ? "meh" : "bad"
      });
      const level_0 = skillLevel({
        firstTry,
        retry: ok_0
      });
      return [level_0, ok_0 ? q.noteHint : q.noteMiss];
    };
    const story = async function story() {
      await say(t(pack.story.prompt), "q");
      const sitIdx = await ask({
        builderStep: 1,
        hint: "Build your answer \xB7 Where",
        options: SITUATIONS.map(_temp5),
        timed: true
      }, false);
      const sit = SITUATIONS[sitIdx];
      let level_1;
      let draft = "";
      if (!sit || sit.stop) {
        level_1 = "missing";
        await say("That\u2019s okay. It\u2019s the one most students don\u2019t have yet.");
      } else {
        push({
          kind: "draft",
          text: `${sit.say}, …`,
          title: "Your answer, so far"
        });
        const act = pickFrom(pack.story.actions, await ask({
          builderStep: 2,
          hint: "Build your answer \xB7 What you did",
          options: pack.story.actions.map(a => t(a.t))
        }, false));
        setMsgs(all_2 => all_2.map(m_2 => m_2.kind === "draft" ? {
          ...m_2,
          text: `${sit.say}, ${t(act.say)}. …`
        } : m_2));
        const res = pickFrom(pack.story.results, await ask({
          builderStep: 3,
          hint: "Build your answer \xB7 The result",
          options: pack.story.results.map(r => t(r.t))
        }, false));
        draft = `${sit.say}, ${t(act.say)}. ${t(res.say)}`;
        setMsgs(all_3 => all_3.map(m_3 => m_3.kind === "draft" ? {
          ...m_3,
          text: draft,
          title: "Your answer"
        } : m_3));
        level_1 = storyLevel({
          actionWeight: act.w ?? 0,
          resultIsGap: res.gap === true,
          resultWeight: res.w ?? 0,
          situationWeight: sit.w ?? 1
        });
        await say(storyReaction({
          actionWeight: act.w ?? 0,
          level: level_1,
          resultIsGap: res.gap === true,
          resultWeight: res.w ?? 0
        }));
      }
      track(LABS_INTERVIEW_EVENTS.STORY_BUILT, {
        level: level_1
      });
      push({
        after: t(pack.ext.line),
        body: level_1 === "ready" ? "Real setting, \u201CI\u201D not \u201Cwe\u201D, and a result. Have the number ready; they will ask." : "Interviewers write down the result. If there isn\u2019t one, there\u2019s nothing to score. This is the part you can\u2019t cram the night before.",
        kind: "coach",
        title: STORY_COACH_TITLE[level_1],
        tone: LEVEL_TONE[level_1],
        yours: draft || undefined
      });
      const storyNotes = {
        missing: "No real example yet. This is the gap.",
        ready: "Real example, \u201CI\u201D not \u201Cwe\u201D, with a result. Have the number ready.",
        shaky: "A start, but thin on what changed. Nothing to write down."
      };
      return [level_1, storyNotes[level_1]];
    };
    const run = async function run() {
      const q0 = pack.qs[0];
      push({
        kind: "sys",
        text: `Round 1 · ${pack.dims[0]}`
      });
      push({
        kind: "q",
        text: t(q0.prompt)
      });
      push({
        kind: "me",
        text: t(q0.options[firstPick] ?? "")
      });
      await pause(350);
      const r1 = await skillRound(0, firstPick);
      await ask({
        options: ["Next question"],
        solid: true
      });
      setRound(1);
      push({
        kind: "sys",
        text: `Round 2 · ${pack.dims[1]}`
      });
      await say(t(pack.qs[1].prompt), "q");
      const r2 = await skillRound(1);
      await ask({
        options: ["Okay, last one"],
        solid: true
      });
      setRound(2);
      push({
        kind: "sys",
        text: "Round 3 \xB7 The one everyone gets"
      });
      const r3 = await story();
      await pause(500);
      await say("That\u2019s everything from me. Thanks for your time.");
      await ask({
        options: ["See my scorecard \u2192"],
        solid: true
      });
      setRound(3);
      onDone([r1[0], r2[0], r3[0]], [r1[1], r2[1], r3[1]]);
    };
    run().catch(_temp6);
    return () => {
      cancelled = true;
      resolver.current = null;
      setMsgs([]);
      setDock(null);
    };
  }, t6);
  let t7;
  if ($[6] !== pack.dims[0] || $[7] !== pack.dims[1]) {
    t7 = [pack.dims[0], pack.dims[1], "Story", "Scorecard"];
    $[6] = pack.dims[0];
    $[7] = pack.dims[1];
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const labels = t7;
  const t8 = "flex min-h-0 flex-1 flex-col";
  const t9 = `flex-none border-[#e5e2e1] border-b pt-3 pr-3.5 pb-2.5 ${closeInset ? "pl-[60px]" : "pl-3.5"}`;
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <InterviewerAvatar />;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <b className="block text-[17px]">Your interviewer</b>;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== styleName) {
    t12 = <div className="min-w-0 flex-1">{t11}<span className="block truncate text-[#5c5a58] text-[14px]">{styleName}</span></div>;
    $[11] = styleName;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== clockKey || $[14] !== dock?.timed) {
    t13 = dock?.timed ? <Clock key={clockKey} /> : <div className="size-[34px] flex-none" />;
    $[13] = clockKey;
    $[14] = dock?.timed;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== t12 || $[17] !== t13) {
    t14 = <div className="flex items-center gap-2.5">{t10}{t12}{t13}</div>;
    $[16] = t12;
    $[17] = t13;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== labels || $[20] !== round) {
    t15 = <div className="mt-2.5 flex gap-1">{labels.map((label_0, k_3) => <span className={`h-1 flex-1 rounded transition-colors ${progressColour(k_3, round)}`} key={label_0} />)}</div>;
    $[19] = labels;
    $[20] = round;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== labels) {
    t16 = <div className="mt-1 flex justify-between font-semibold text-[#8a8785] text-[12px]">{labels.map(_temp7)}</div>;
    $[22] = labels;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== t14 || $[25] !== t15 || $[26] !== t16 || $[27] !== t9) {
    t17 = <div className={t9}>{t14}{t15}{t16}</div>;
    $[24] = t14;
    $[25] = t15;
    $[26] = t16;
    $[27] = t9;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== msgs) {
    t18 = msgs.map(_temp8);
    $[29] = msgs;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t18) {
    t19 = <div className="flex flex-1 flex-col gap-2 overflow-y-auto bg-gradient-to-b from-white via-white to-[#fbfaf7] px-3.5 pt-4 pb-2.5" ref={threadRef}>{t18}</div>;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  const t20 = "grid min-h-16 flex-none gap-2 border-[#e5e2e1] border-t bg-white px-3.5 pt-2.5 pb-4";
  const t21 = dock ? <><DockHeader dock={dock} />{dock.options.map((option, i_1) => <button className={dock.solid ? "xi-pop min-h-12 rounded-full bg-[#0c1117] px-4 font-semibold text-[17px] text-white" : "xi-pop flex items-start gap-3 rounded-2xl border-[#e6e7ea] border-[1.5px] bg-white px-3.5 py-3 text-left font-semibold text-[#0c1117] text-[16px] leading-[1.35] transition-colors hover:border-[#4ecb5f] hover:bg-[#f5fcf6] active:bg-[#effaf1]"} key={option} onClick={() => pick(i_1, option)} type="button">{dock.solid ? option : <><span className="grid size-8 flex-none place-items-center rounded-[10px] bg-[#f1f2f4] font-bold text-[#4b5563] text-[13px]">{"ABCD"[i_1]}</span><span className="pt-1">{option}</span></>}</button>)}</> : null;
  let t22;
  if ($[33] !== t21) {
    t22 = <div className={t20}>{t21}</div>;
    $[33] = t21;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== t17 || $[36] !== t19 || $[37] !== t22) {
    t23 = <div className={t8}>{t17}{t19}{t22}</div>;
    $[35] = t17;
    $[36] = t19;
    $[37] = t22;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  return t23;
}
function _temp8(m_4) {
  return <Message key={m_4.id} m={m_4} />;
}
function _temp7(label_1) {
  return <span key={label_1}>{label_1}</span>;
}
function _temp6() {}
function _temp5(s) {
  return s.t;
}
function _temp4(_, k_0) {
  return k_0;
}
function _temp3(k) {
  return k + 1;
}
function DockHeader(t0) {
  const $ = _c(11);
  const {
    dock
  } = t0;
  if (dock.builderStep) {
    let t1;
    if ($[0] !== dock.hint) {
      t1 = <span>{dock.hint}</span>;
      $[0] = dock.hint;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = [1, 2, 3];
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== dock.builderStep) {
      t3 = <span className="flex gap-1">{t2.map(k => <i className={`h-1 w-[18px] rounded ${k <= (dock.builderStep ?? 0) ? "bg-[#4ecb5f]" : "bg-[#e6e7ea]"}`} key={k} />)}</span>;
      $[3] = dock.builderStep;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== t1 || $[6] !== t3) {
      t4 = <div className="flex items-center justify-between font-extrabold text-[#5c5a58] text-[12px] uppercase tracking-[.06em]">{t1}{t3}</div>;
      $[5] = t1;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  if (!dock.hint) {
    return null;
  }
  const t1 = dock.tip ? "rounded-[10px] bg-[#fff8ea] px-2.5 py-2 font-semibold text-[#9a6700] text-[14px]" : "text-center font-medium text-[#5c5a58] text-[14px]";
  let t2;
  if ($[8] !== dock.hint || $[9] !== t1) {
    t2 = <div className={t1}>{dock.hint}</div>;
    $[8] = dock.hint;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
function InterviewerAvatar() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="relative grid size-10 flex-none place-items-center rounded-full bg-[#34506b] text-white"><svg aria-hidden="true" fill="none" height="20" viewBox="0 0 24 24" width="20"><circle cx="12" cy="8" fill="currentColor" r="4" /><path d="M4 20c0-4 3.6-6 8-6s8 2 8 6" fill="currentColor" /></svg><span className="absolute right-[-1px] bottom-[-1px] size-2.5 rounded-full border-2 border-white bg-[#3fbf5a]" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/** A soft 30-second ring. Never cuts anyone off; it only goes amber. */
function Clock() {
  const $ = _c(12);
  const [s, setS] = useState(30);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const id = setInterval(() => setS(_temp9), 1000);
      return () => clearInterval(id);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const late = s <= 10;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <circle cx="17" cy="17" fill="none" r="14" stroke="#e5e2e1" strokeWidth="3" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = 88 * (1 - s / 30);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      transition: "stroke-dashoffset 1s linear"
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3) {
    t5 = <svg aria-hidden="true" className="-rotate-90" height="34" width="34">{t2}<circle cx="17" cy="17" fill="none" r="14" stroke="#1c1b1b" strokeDasharray="88" strokeDashoffset={t3} strokeLinecap="round" strokeWidth="3" style={t4} /></svg>;
    $[4] = t3;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = `absolute inset-0 grid place-items-center font-bold text-[12px] tabular-nums ${late ? "text-[#9a6700]" : "text-[#5c5a58]"}`;
  const t7 = s > 0 ? s : "\u2026";
  let t8;
  if ($[6] !== t6 || $[7] !== t7) {
    t8 = <span className={t6}>{t7}</span>;
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t5 || $[10] !== t8) {
    t9 = <div aria-hidden="true" className="relative size-[34px] flex-none">{t5}{t8}</div>;
    $[9] = t5;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  return t9;
}
function _temp9(v) {
  return v > 0 ? v - 1 : 0;
}
function Message(t0) {
  const $ = _c(24);
  const {
    m
  } = t0;
  if (m.kind === "typing") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex gap-1 self-start rounded-[20px] bg-[#f6f4f2] px-4 py-3.5">{[0, 0.15, 0.3].map(_temp10)}</div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (m.kind === "sys") {
    let t1;
    if ($[1] !== m.text) {
      t1 = <div className="mt-2 mb-0.5 self-center font-semibold text-[#8a8785] text-[12px] uppercase tracking-[.04em]">{m.text}</div>;
      $[1] = m.text;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (m.kind === "me") {
    let t1;
    if ($[3] !== m.text) {
      t1 = <div className="xi-pop max-w-[84%] self-end rounded-[20px] rounded-br-md bg-[#0c1117] px-3.5 py-2.5 text-[17px] text-white leading-[1.42]">{m.text}</div>;
      $[3] = m.text;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  if (m.kind === "draft") {
    let t1;
    if ($[5] !== m.title) {
      t1 = <span className="mb-1 block font-bold text-[#2f9a45] text-[12px] uppercase tracking-[.06em]">{m.title}</span>;
      $[5] = m.title;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    let t2;
    if ($[7] !== m.text || $[8] !== t1) {
      t2 = <div className="xi-pop max-w-[88%] self-end rounded-[20px] rounded-br-md border-[#4ecb5f] border-[1.5px] border-dashed bg-[#f5fcf6] px-3.5 py-2.5 text-[17px] leading-[1.42]">{t1}{m.text}</div>;
      $[7] = m.text;
      $[8] = t1;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  if (m.kind === "coach") {
    let t1;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        bad: "border-[#f0b4ad] bg-[#fff4f3] [&_.ct]:text-[#b42318]",
        good: "border-[#b9e4bd] bg-[#effbee] [&_.ct]:text-[#1f6b2a]",
        meh: "border-[#ecd09a] bg-[#fff8ea] [&_.ct]:text-[#9a6700]"
      };
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    const tone = t1[m.tone ?? "meh"];
    const t2 = `xi-pop self-stretch rounded-2xl border px-3.5 py-3 text-[15px] leading-[1.5] ${tone}`;
    let t3;
    if ($[11] !== m.title) {
      t3 = <div className="ct mb-1 font-extrabold text-[12px] uppercase tracking-[.05em]">{m.title}</div>;
      $[11] = m.title;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    let t4;
    if ($[13] !== m.after || $[14] !== m.yours) {
      t4 = m.after ? <div className="mt-2.5 grid gap-2">{m.yours ? <div className="rounded-xl bg-white px-3 py-2.5 text-[15px]"><small className="mb-1 block font-extrabold text-[#8a8785] text-[12px] uppercase tracking-[.06em]">What you’d say today</small>{m.yours}</div> : null}<div className="rounded-xl border-[#4ecb5f] border-l-[3px] bg-white px-3 py-2.5 text-[15px]"><small className="mb-1 block font-extrabold text-[#2f9a45] text-[12px] uppercase tracking-[.06em]">After an Extern externship</small>“{m.after}”</div></div> : null;
      $[13] = m.after;
      $[14] = m.yours;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    let t5;
    if ($[16] !== m.body || $[17] !== t2 || $[18] !== t3 || $[19] !== t4) {
      t5 = <div className={t2}>{t3}{m.body}{t4}</div>;
      $[16] = m.body;
      $[17] = t2;
      $[18] = t3;
      $[19] = t4;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    return t5;
  }
  const t1 = `xi-pop max-w-[84%] self-start rounded-[20px] rounded-bl-md px-3.5 py-2.5 leading-[1.42] ${m.kind === "q" ? "border border-[#e6e7ea] bg-white font-dm text-[22px] leading-[1.25] shadow-[0_2px_10px_rgba(0,0,0,.04)]" : "bg-[#f6f4f2] text-[17px]"}`;
  let t2;
  if ($[21] !== m.text || $[22] !== t1) {
    t2 = <div className={t1}>{m.text}</div>;
    $[21] = m.text;
    $[22] = t1;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  return t2;
}

/* ---------------------------------------------------------------- review */
function _temp10(d) {
  return <i className="size-[7px] rounded-full bg-[#8a8785]" key={d} style={{
    animation: `xi-blink 1s ${d}s infinite`
  }} />;
}
function Review(t0) {
  const $ = _c(11);
  const {
    dims,
    onDone
  } = t0;
  let t1;
  if ($[0] !== dims) {
    t1 = [...dims, "Overall decision"];
    $[0] = dims;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rows = t1;
  const [done, setDone] = useState(0);
  let t2;
  if ($[2] !== onDone || $[3] !== rows) {
    t2 = () => {
      const step = prefersReducedMotion() ? 60 : 480;
      const timers = rows.map((_, i) => setTimeout(() => setDone(i + 1), step * (i + 1)));
      timers.push(setTimeout(onDone, step * rows.length + 450));
      return () => timers.forEach(clearTimeout);
    };
    $[2] = onDone;
    $[3] = rows;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <InterviewerAvatar />;
    t5 = <h3 className="mt-4 mb-5 font-bold text-[21px] tracking-[-.3px]">Filling in your scorecard…</h3>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  let t6;
  if ($[8] !== done || $[9] !== rows) {
    t6 = <div className="xi-pop flex flex-1 flex-col items-center justify-center p-8 text-center">{t4}{t5}<div className="grid w-full max-w-[260px] gap-2.5">{rows.map((row, i_0) => <div className={`flex items-center gap-2.5 text-[16px] transition-colors ${i_0 < done ? "text-[#1c1b1b]" : "text-[#8a8785]"}`} key={row}><i className={`grid size-5 flex-none place-items-center rounded-full border-2 text-[12px] not-italic transition-all ${i_0 < done ? "border-[#1c1b1b] bg-[#1c1b1b] text-white" : "border-[#c9c6c5]"}`}>✓</i>{row}</div>)}</div></div>;
    $[8] = done;
    $[9] = rows;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}

/* ---------------------------------------------------------------- scorecard */

function Scorecard(t0) {
  const $ = _c(131);
  const {
    closeInset,
    company,
    embedded,
    guideSource,
    levels,
    notes,
    onRestart,
    pack,
    styleName,
    t,
    track,
    visitorId
  } = t0;
  let t1;
  if ($[0] !== levels) {
    t1 = decide(levels);
    $[0] = levels;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const decision = t1;
  const storyReady = levels[2] === "ready";
  const skillsStrong = levels[0] === "ready" && levels[1] === "ready";
  const allReady = levels.every(_temp11);
  let t2;
  if ($[2] !== levels || $[3] !== storyReady) {
    t2 = storyReady ? levels.findIndex(_temp12) : 2;
    $[2] = levels;
    $[3] = storyReady;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const gapIdx = t2;
  const strengthIdx = levels.indexOf("ready");
  const [email, setEmail] = useState(null);
  const [newsletter, setNewsletter] = useState(false);
  const name = company ?? "the firm";
  const completed = useRef(false);
  let t3;
  let t4;
  if ($[5] !== decision || $[6] !== levels || $[7] !== track) {
    t3 = () => {
      if (completed.current) {
        return;
      }
      completed.current = true;
      track(LABS_INTERVIEW_EVENTS.COMPLETED, {
        decision,
        levels
      });
    };
    t4 = [decision, levels, track];
    $[5] = decision;
    $[6] = levels;
    $[7] = track;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  const title = scorecardTitle({
    allReady,
    skillsStrong,
    storyReady
  });
  let t5;
  if ($[10] !== pack.dims[0] || $[11] !== pack.dims[1] || $[12] !== storyReady) {
    t5 = storyReady ? "The part most students can\u2019t fake, you have. Tighten the rows below." : `You can prep ${pack.dims[0].toLowerCase()} and ${pack.dims[1].toLowerCase()} this week. The story takes a real project.`;
    $[10] = pack.dims[0];
    $[11] = pack.dims[1];
    $[12] = storyReady;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const sub = t5;
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      missing: 1,
      ready: 3,
      shaky: 2
    };
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const dots = t6;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      advance: "border-[#1f6b2a] text-[#1f6b2a]",
      borderline: "border-[#9a6700] text-[#9a6700]",
      "not-yet": "border-[#2853d2] text-[#2853d2]"
    };
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const stampTone = t7;
  let t8;
  if ($[16] !== decision || $[17] !== guideSource || $[18] !== newsletter || $[19] !== pack.key || $[20] !== setEmail || $[21] !== track || $[22] !== visitorId) {
    t8 = e => {
      e.preventDefault();
      const value = String(new FormData(e.currentTarget).get("email") ?? "").trim();
      if (!value) {
        return;
      }
      submitPreviewLead({
        archetype: `interview-${decision}`,
        email: value,
        guideSource,
        marketingConsent: newsletter,
        vertical: `interview-${pack.key}`,
        visitorId
      });
      track(LABS_INTERVIEW_EVENTS.EMAIL_SUBMITTED, {
        decision,
        newsletter
      });
      setEmail(value);
    };
    $[16] = decision;
    $[17] = guideSource;
    $[18] = newsletter;
    $[19] = pack.key;
    $[20] = setEmail;
    $[21] = track;
    $[22] = visitorId;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const unlock = t8;
  const t9 = email ?? undefined;
  const t10 = email ? newsletter : undefined;
  let t11;
  if ($[24] !== company || $[25] !== guideSource || $[26] !== levels || $[27] !== pack.ext.externshipId || $[28] !== pack.key || $[29] !== t10 || $[30] !== t9) {
    t11 = interviewSignUpHref({
      company,
      email: t9,
      externshipId: pack.ext.externshipId,
      guideSource,
      levels,
      newsletter: t10,
      pack: pack.key
    });
    $[24] = company;
    $[25] = guideSource;
    $[26] = levels;
    $[27] = pack.ext.externshipId;
    $[28] = pack.key;
    $[29] = t10;
    $[30] = t9;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const href = t11;
  const t12 = `xi-pop flex-1 overflow-y-auto bg-[#f6f4f2] px-4 pb-7 ${closeInset ? "pt-[60px]" : "pt-[18px]"}`;
  let t13;
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <InterviewerAvatar />;
    t14 = <b className="block text-[16px]">Your interviewer’s scorecard</b>;
    $[32] = t13;
    $[33] = t14;
  } else {
    t13 = $[32];
    t14 = $[33];
  }
  let t15;
  if ($[34] !== styleName) {
    t15 = <div className="mb-3.5 flex items-center gap-2.5">{t13}<div>{t14}<span className="text-[#5c5a58] text-[14px]">{styleName} · practice</span></div></div>;
    $[34] = styleName;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const t16 = `inline-block rounded-lg border-2 px-3 py-1.5 font-extrabold text-[14px] uppercase tracking-[.06em] ${stampTone[decision]}`;
  let t17;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      animation: `xi-stamp .45s ${EASE} both .15s`,
      transform: "rotate(-2.5deg)"
    };
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const t18 = DECISION_LABEL[decision];
  let t19;
  if ($[37] !== t16 || $[38] !== t18) {
    t19 = <span className={t16} style={t17}>{t18}</span>;
    $[37] = t16;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== title) {
    t20 = <h2 className="mt-3 mb-1.5 font-extrabold text-[30px] leading-[1.12] tracking-[-.8px]">{title}</h2>;
    $[40] = title;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== sub) {
    t21 = <p className="text-[#5c5a58] text-[16px] leading-[1.45]">{sub}</p>;
    $[42] = sub;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  let t23;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <span>★</span>;
    t23 = <b className="text-[#1f6b2a]">What you did well: </b>;
    $[44] = t22;
    $[45] = t23;
  } else {
    t22 = $[44];
    t23 = $[45];
  }
  const t24 = strengthIdx >= 0 ? notes[strengthIdx] : "you finished a real first-round format. Most applicants never practise one out loud.";
  let t25;
  if ($[46] !== t24) {
    t25 = <div className="mt-3.5 flex gap-2.5 rounded-[14px] bg-[#effbee] px-3 py-2.5 text-[15px] leading-[1.45]">{t22}<span>{t23}{t24}</span></div>;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== t15 || $[49] !== t19 || $[50] !== t20 || $[51] !== t21 || $[52] !== t25) {
    t26 = <div className="mb-3 rounded-[22px] border border-[#e5e2e1] bg-white p-[18px]">{t15}{t19}{t20}{t21}{t25}</div>;
    $[48] = t15;
    $[49] = t19;
    $[50] = t20;
    $[51] = t21;
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== gapIdx || $[55] !== levels || $[56] !== notes || $[57] !== pack.dims) {
    let t28;
    if ($[59] !== gapIdx || $[60] !== levels || $[61] !== notes) {
      t28 = (dim, i) => <div className="grid grid-cols-[1fr_auto] gap-x-2.5 gap-y-0.5 border-[#e5e2e1] border-t py-3 first:border-t-0 first:pt-1" key={dim}><b className="font-sans text-[16px]">{dim}{i === gapIdx && levels[i] !== "ready" ? <span className="ml-2 rounded-md bg-[#dce1ff] px-1.5 py-0.5 align-[2px] font-extrabold text-[#2853d2] text-[12px] uppercase tracking-[.05em]">The fix</span> : null}</b><span aria-label={`${dots[levels[i] ?? "missing"]} of 3`} className="flex items-center gap-1" role="img">{[1, 2, 3].map(k => <i className={`size-[9px] rounded-full ${k <= dots[levels[i] ?? "missing"] ? "bg-[#1c1b1b]" : "bg-[#e5e2e1]"}`} key={k} />)}</span><span className="col-span-2 text-[#5c5a58] text-[16px] italic leading-[1.4]">“{notes[i]}”</span></div>;
      $[59] = gapIdx;
      $[60] = levels;
      $[61] = notes;
      $[62] = t28;
    } else {
      t28 = $[62];
    }
    t27 = pack.dims.map(t28);
    $[54] = gapIdx;
    $[55] = levels;
    $[56] = notes;
    $[57] = pack.dims;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[63] !== t27) {
    t28 = <div className="mb-3 rounded-[22px] border border-[#e5e2e1] bg-white p-[18px] font-serif">{t27}</div>;
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== email || $[66] !== name || $[67] !== newsletter || $[68] !== pack.ext.line || $[69] !== pack.extra || $[70] !== pack.qs || $[71] !== setNewsletter || $[72] !== t || $[73] !== unlock) {
    t29 = <div className="mb-3 rounded-[22px] border border-[#e5e2e1] bg-white p-[18px]">{email ? <><h4 className="mb-1 font-bold text-[19px] tracking-[-.3px]">Unlocked. A copy is on its way to {email}</h4><p className="mb-3 text-[#5c5a58] text-[15px]">Here’s what the interviewer was listening for.</p><div className="grid gap-2">{pack.qs.map(q => <ModelAnswer key={q.skill} label={q.skill} text={`${t(q.why)} ${t(q.fu.why)}`} />)}<ModelAnswer label="Your story, rebuilt" text={t(pack.ext.line)} />{pack.extra.map((x, i_0) => <ModelAnswer key={x.q} label={`Practice question ${i_0 + 1}: ${t(x.q)}`} text={t(x.model)} />)}</div></> : <><h4 className="mb-1 font-bold text-[19px] tracking-[-.3px]">See what a strong answer sounds like</h4><p className="mb-3 text-[#5c5a58] text-[15px] leading-[1.45]">Model answers for all three questions, plus {pack.extra.length} more {name}-style questions you didn’t get today.</p><form className="flex gap-2" onSubmit={unlock}><input aria-label="Email" autoComplete="email" className="h-[46px] min-w-0 flex-1 rounded-full border-[#c9c6c5] border-[1.5px] px-4 text-[17px] focus:border-[#2853d2] focus:outline-none" name="email" placeholder="you@school.edu" required={true} type="email" /><button className="min-h-[46px] rounded-full bg-[#0c1117] px-[18px] font-semibold text-[17px] text-white" type="submit">Unlock</button></form><label className="mt-2.5 flex items-start gap-2 text-[#5c5a58] text-[14px] leading-[1.4]"><input checked={newsletter} className="mt-0.5" onChange={e_0 => setNewsletter(e_0.target.checked)} type="checkbox" />Also send me internship tips from Extern. Optional.</label></>}</div>;
    $[65] = email;
    $[66] = name;
    $[67] = newsletter;
    $[68] = pack.ext.line;
    $[69] = pack.extra;
    $[70] = pack.qs;
    $[71] = setNewsletter;
    $[72] = t;
    $[73] = unlock;
    $[74] = t29;
  } else {
    t29 = $[74];
  }
  const t30 = gapIdx === 2 ? "The fix for your story" : "Make your story stronger";
  let t31;
  if ($[75] !== t30) {
    t31 = <div className="font-extrabold text-[#2853d2] text-[12px] uppercase tracking-[.06em]">{t30}</div>;
    $[75] = t30;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  let t32;
  if ($[77] !== name) {
    t32 = <h4 className="mt-1.5 mb-3 font-extrabold text-[22px] leading-[1.2]">Walk into {name} with a real project to talk about.</h4>;
    $[77] = name;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  let t33;
  if ($[79] !== pack.ext.color) {
    t33 = {
      background: pack.ext.color
    };
    $[79] = pack.ext.color;
    $[80] = t33;
  } else {
    t33 = $[80];
  }
  let t34;
  if ($[81] !== pack.ext.mark || $[82] !== t33) {
    t34 = <div className="grid size-[38px] flex-none place-items-center rounded-[10px] font-extrabold text-[17px] text-white" style={t33}>{pack.ext.mark}</div>;
    $[81] = pack.ext.mark;
    $[82] = t33;
    $[83] = t34;
  } else {
    t34 = $[83];
  }
  let t35;
  if ($[84] !== pack.ext.title) {
    t35 = <b className="block text-[15px] leading-[1.3]">{pack.ext.title}</b>;
    $[84] = pack.ext.title;
    $[85] = t35;
  } else {
    t35 = $[85];
  }
  let t36;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <span className="text-[#5c5a58] text-[14px]">A short project for a real company (we call it an externship). Online, on your schedule.</span>;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  let t37;
  if ($[87] !== t35) {
    t37 = <div>{t35}{t36}</div>;
    $[87] = t35;
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  let t38;
  if ($[89] !== t34 || $[90] !== t37) {
    t38 = <div className="mb-2.5 flex items-center gap-2.5">{t34}{t37}</div>;
    $[89] = t34;
    $[90] = t37;
    $[91] = t38;
  } else {
    t38 = $[91];
  }
  let t39;
  if ($[92] !== pack.ext.steps) {
    t39 = pack.ext.steps.map(_temp13);
    $[92] = pack.ext.steps;
    $[93] = t39;
  } else {
    t39 = $[93];
  }
  let t40;
  if ($[94] !== t39) {
    t40 = <ol className="grid gap-[7px]">{t39}</ol>;
    $[94] = t39;
    $[95] = t40;
  } else {
    t40 = $[95];
  }
  let t41;
  if ($[96] !== t38 || $[97] !== t40) {
    t41 = <div className="mb-2.5 rounded-2xl bg-white p-3.5">{t38}{t40}</div>;
    $[96] = t38;
    $[97] = t40;
    $[98] = t41;
  } else {
    t41 = $[98];
  }
  let t42;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <small className="mb-1 block font-extrabold text-[#8a8785] text-[12px] uppercase tracking-[.06em]">When they ask round 3, you say</small>;
    $[99] = t42;
  } else {
    t42 = $[99];
  }
  let t43;
  if ($[100] !== pack.ext.line || $[101] !== t) {
    t43 = t(pack.ext.line);
    $[100] = pack.ext.line;
    $[101] = t;
    $[102] = t43;
  } else {
    t43 = $[102];
  }
  let t44;
  if ($[103] !== t43) {
    t44 = <div className="mb-3 rounded-r-xl border-[#2853d2] border-l-[3px] bg-white px-3 py-2.5 text-[15px] leading-[1.5]">{t42}“{t43}”</div>;
    $[103] = t43;
    $[104] = t44;
  } else {
    t44 = $[104];
  }
  let t45;
  if ($[105] !== decision || $[106] !== email || $[107] !== track) {
    t45 = () => track(LABS_INTERVIEW_EVENTS.SIGNUP_HANDOFF, {
      decision,
      path: "externship_card",
      with_email: Boolean(email)
    });
    $[105] = decision;
    $[106] = email;
    $[107] = track;
    $[108] = t45;
  } else {
    t45 = $[108];
  }
  const t46 = embedded ? "_top" : undefined;
  let t47;
  if ($[109] !== href || $[110] !== t45 || $[111] !== t46) {
    t47 = <a className="flex min-h-[46px] items-center justify-center rounded-full bg-[#2853d2] px-[18px] font-semibold text-[17px] text-white" href={href} onClick={t45} rel="noopener" target={t46}>Start this project, free</a>;
    $[109] = href;
    $[110] = t45;
    $[111] = t46;
    $[112] = t47;
  } else {
    t47 = $[112];
  }
  let t48;
  if ($[113] !== t31 || $[114] !== t32 || $[115] !== t41 || $[116] !== t44 || $[117] !== t47) {
    t48 = <div className="mb-3 rounded-[22px] bg-gradient-to-br from-[#eafbe8] to-[#e6eaff] p-[18px]">{t31}{t32}{t41}{t44}{t47}</div>;
    $[113] = t31;
    $[114] = t32;
    $[115] = t41;
    $[116] = t44;
    $[117] = t47;
    $[118] = t48;
  } else {
    t48 = $[118];
  }
  let t49;
  if ($[119] !== onRestart) {
    t49 = <button className="mx-auto block py-2 font-medium text-[#5c5a58] text-[16px]" onClick={onRestart} type="button">Try the interview again</button>;
    $[119] = onRestart;
    $[120] = t49;
  } else {
    t49 = $[120];
  }
  const t50 = company ? ` Not affiliated with or endorsed by ${company}.` : "";
  let t51;
  if ($[121] !== t50) {
    t51 = <p className="mt-2 text-center text-[#8a8785] text-[12px] leading-[1.4]">Practice interview modelled on common first-round formats.{t50}</p>;
    $[121] = t50;
    $[122] = t51;
  } else {
    t51 = $[122];
  }
  let t52;
  if ($[123] !== t12 || $[124] !== t26 || $[125] !== t28 || $[126] !== t29 || $[127] !== t48 || $[128] !== t49 || $[129] !== t51) {
    t52 = <div className={t12}>{t26}{t28}{t29}{t48}{t49}{t51}</div>;
    $[123] = t12;
    $[124] = t26;
    $[125] = t28;
    $[126] = t29;
    $[127] = t48;
    $[128] = t49;
    $[129] = t51;
    $[130] = t52;
  } else {
    t52 = $[130];
  }
  return t52;
}
function _temp13(step, i_1) {
  return <li className="flex gap-2 text-[14px] leading-[1.4]" key={step}><span className="grid size-[18px] flex-none place-items-center rounded-full bg-[#dce1ff] font-extrabold text-[#2853d2] text-[12px]">{i_1 + 1}</span>{step}</li>;
}
function _temp12(l_0) {
  return l_0 !== "ready";
}
function _temp11(l) {
  return l === "ready";
}
function ModelAnswer(t0) {
  const $ = _c(5);
  const {
    label,
    text
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <small className="mb-0.5 block font-extrabold text-[#1f6b2a] text-[12px] uppercase tracking-[.06em]">{label}</small>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1 || $[3] !== text) {
    t2 = <div className="rounded-xl bg-[#f6f4f2] px-3 py-2.5 text-[15px] leading-[1.45]">{t1}{text}</div>;
    $[2] = t1;
    $[3] = text;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}